export type TransportState =
  | "connecting"
  | "ready"
  | "reconnecting"
  | "offline"
  | "session-expired"
  | "access-denied";

export type AccessResult = "retry" | "session-expired" | "access-denied";

export interface RealtimeConnectionOptions {
  getTicket(signal: AbortSignal): Promise<{ ticket: string; url: string }>;
  onState(state: TransportState): void;
  onReady(): void;
  onDirectory(): void;
  onChat(chatId: string): void;
  onTyping(frame: Record<string, unknown>): void;
  onReset(): void;
  verifyAccess(chatId: string | null): Promise<AccessResult>;
  socketFactory?: (url: string) => WebSocket;
}

const DEADLINE_MS = 10_000;
const HEARTBEAT_MS = 10_000;
const MAX_BUFFER_BYTES = 65_536;
const MAX_FRAME_BYTES = 8_192;
type Timer = ReturnType<typeof setTimeout>;

function safeSocketUrl(value: string): string {
  const url = new URL(value);
  const loopback =
    url.hostname === "localhost" ||
    url.hostname === "[::1]" ||
    /^127\.\d+\.\d+\.\d+$/.test(url.hostname);
  if (
    (url.protocol !== "wss:" && !(url.protocol === "ws:" && loopback)) ||
    url.username ||
    url.password
  )
    throw new Error("A secure relay URL is required.");
  return url.href;
}

function authorizationError(error: unknown): boolean {
  if (!error || typeof error !== "object" || !("status" in error)) return false;
  return error.status === 401 || error.status === 403;
}

/** One signed-in tab's disposable hint channel. Ready never implies data is current. */
export class RealtimeConnection {
  private readonly options: RealtimeConnectionOptions;
  private stopped = true;
  private online = true;
  private generation = 0;
  private state: TransportState | null = null;
  private permanent: Exclude<AccessResult, "retry"> | null = null;
  private chatId: string | null = null;
  private requestedChat: string | null = null;
  private subscribedChat: string | null = null;
  private socket: WebSocket | null = null;
  private ticketAbort: AbortController | null = null;
  private checkingAccess = false;
  private hello = false;
  private ready = false;
  private hasAttempted = false;
  private attempts = 0;
  private phaseTimer?: Timer;
  private subscriptionTimer?: Timer;
  private heartbeatTimer?: Timer;
  private pongTimer?: Timer;
  private retryTimer?: Timer;
  private pingSentAt: number | null = null;

  constructor(options: RealtimeConnectionOptions) {
    this.options = options;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.permanent = null;
    this.hasAttempted = false;
    this.attempts = 0;
    if (this.online) void this.connect();
    else this.setState("offline");
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.disposeConnection();
    this.state = null;
  }

  setChat(chatId: string | null): void {
    if (chatId === this.chatId) return;
    this.chatId = chatId;
    this.ready = false;
    this.options.onReset();
    if (this.permanent === "session-expired") return;
    // A denial belongs to the selected room; a new room gets a fresh check.
    if (this.permanent === "access-denied" || this.checkingAccess) {
      this.permanent = null;
      this.disposeConnection();
      if (this.canConnect()) void this.connect();
      else if (!this.stopped && !this.online) this.setState("offline");
      return;
    }
    if (!this.hello) return;
    this.setState("connecting");
    this.reconcileSubscription();
  }

  setOnline(online: boolean): void {
    if (online === this.online) return;
    this.online = online;
    if (this.stopped) return;
    if (!online) {
      this.disposeConnection();
      if (!this.permanent) this.setState("offline");
    } else if (!this.permanent) void this.connect();
  }

  /** Called on foreground resume; probes even sockets that still report OPEN. */
  refresh(): void {
    if (!this.canConnect()) return;
    if (this.hello) {
      if (
        this.pingSentAt !== null &&
        Date.now() - this.pingSentAt >= DEADLINE_MS
      ) {
        this.disposeConnection();
        void this.connect();
      } else this.ping();
    } else if (!this.socket && !this.ticketAbort && !this.checkingAccess) {
      clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
      void this.connect();
    }
  }

  sendTyping(active: boolean): void {
    if (!this.ready || !this.chatId || this.subscribedChat !== this.chatId)
      return;
    this.send({
      v: 1,
      type: "typing",
      chatId: this.chatId,
      state: active ? "start" : "stop",
    });
  }

  private canConnect(): boolean {
    return !this.stopped && this.online && !this.permanent;
  }

  private current(generation: number): boolean {
    return this.canConnect() && this.generation === generation;
  }

  private setState(state: TransportState): void {
    if (state === this.state) return;
    this.state = state;
    this.options.onState(state);
  }

  private armPhase(generation: number): void {
    clearTimeout(this.phaseTimer);
    this.phaseTimer = setTimeout(() => {
      if (this.current(generation)) this.fail();
    }, DEADLINE_MS);
  }

  private async connect(): Promise<void> {
    if (
      !this.canConnect() ||
      this.socket ||
      this.ticketAbort ||
      this.checkingAccess
    )
      return;
    clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    const generation = ++this.generation;
    this.setState(this.hasAttempted ? "reconnecting" : "connecting");
    this.hasAttempted = true;
    const abort = new AbortController();
    this.ticketAbort = abort;
    this.armPhase(generation);
    try {
      const ticket = await this.options.getTicket(abort.signal);
      if (!this.current(generation)) return;
      this.ticketAbort = null;
      const url = safeSocketUrl(ticket.url);
      const socket = this.options.socketFactory
        ? this.options.socketFactory(url)
        : new WebSocket(url);
      this.socket = socket;
      this.armPhase(generation);
      let opened = false;
      socket.onopen = () => {
        if (!this.current(generation) || socket !== this.socket || opened)
          return;
        opened = true;
        // Authentication must remain the first frame, before any subscriptions.
        if (this.send({ v: 1, type: "auth", ticket: ticket.ticket }))
          this.armPhase(generation);
      };
      socket.onmessage = (event) => {
        if (this.current(generation) && socket === this.socket)
          this.receive(event.data);
      };
      socket.onerror = () => {
        if (this.current(generation) && socket === this.socket) this.fail();
      };
      socket.onclose = (event) => {
        if (!this.current(generation) || socket !== this.socket) return;
        if (event.code === 4401 || event.code === 4403) this.checkAccess();
        else this.fail();
      };
    } catch (error) {
      if (!this.current(generation)) return;
      if (authorizationError(error)) this.checkAccess();
      else this.fail();
    }
  }

  private receive(raw: unknown): void {
    let frame: Record<string, unknown>;
    try {
      if (typeof raw !== "string" || raw.length > MAX_BUFFER_BYTES)
        throw new Error("Invalid relay frame.");
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
        throw new Error("Invalid relay frame.");
      frame = parsed as Record<string, unknown>;
    } catch {
      this.fail();
      return;
    }
    if (frame.type === "hello") {
      if (this.hello) return;
      clearTimeout(this.phaseTimer);
      this.hello = true;
      this.scheduleHeartbeat();
      this.reconcileSubscription();
      return;
    }
    if (!this.hello) return;
    if (frame.type === "pong") {
      if (this.pingSentAt === null) return;
      clearTimeout(this.pongTimer);
      this.pongTimer = undefined;
      this.pingSentAt = null;
      this.scheduleHeartbeat();
    } else if (frame.type === "directory.changed") {
      this.options.onDirectory();
    } else if (
      frame.type === "subscribed" &&
      this.requestedChat !== null &&
      frame.chatId === this.requestedChat
    ) {
      clearTimeout(this.subscriptionTimer);
      this.subscriptionTimer = undefined;
      this.subscribedChat = this.requestedChat;
      this.requestedChat = null;
      this.reconcileSubscription();
    } else if (
      this.ready &&
      this.chatId !== null &&
      this.subscribedChat === this.chatId &&
      frame.chatId === this.chatId
    ) {
      if (frame.type === "chat.changed") this.options.onChat(this.chatId);
      else if (frame.type === "typing") this.options.onTyping(frame);
    }
  }

  private reconcileSubscription(): void {
    if (!this.hello) return;
    // Serialize pending subscriptions: the protocol has no request IDs, so
    // A -> B -> A must not mistake an old A acknowledgement for the new one.
    if (this.requestedChat !== null) return;
    if (this.subscribedChat && this.subscribedChat !== this.chatId) {
      if (
        !this.send({ v: 1, type: "unsubscribe", chatId: this.subscribedChat })
      )
        return;
      this.subscribedChat = null;
    }
    if (this.chatId === null || this.subscribedChat === this.chatId) {
      if (this.ready) return;
      this.ready = true;
      this.attempts = 0;
      this.setState("ready");
      this.options.onReady();
      return;
    }
    this.requestedChat = this.chatId;
    const generation = this.generation;
    this.subscriptionTimer = setTimeout(() => {
      if (this.current(generation)) this.fail();
    }, DEADLINE_MS);
    this.send({ v: 1, type: "subscribe", chatId: this.chatId });
  }

  private send(frame: Record<string, unknown>): boolean {
    const socket = this.socket;
    if (!this.canConnect() || !socket) return false;
    if (socket.readyState !== 1) {
      this.fail();
      return false;
    }
    const encoded = JSON.stringify(frame);
    const bytes = new TextEncoder().encode(encoded).byteLength;
    if (
      bytes > MAX_FRAME_BYTES ||
      socket.bufferedAmount + bytes > MAX_BUFFER_BYTES
    ) {
      this.fail();
      return false;
    }
    try {
      socket.send(encoded);
      return true;
    } catch {
      this.fail();
      return false;
    }
  }

  private scheduleHeartbeat(): void {
    clearTimeout(this.heartbeatTimer);
    const generation = this.generation;
    this.heartbeatTimer = setTimeout(() => {
      if (this.current(generation)) this.ping();
    }, HEARTBEAT_MS);
  }

  private ping(): void {
    if (!this.hello || this.pingSentAt !== null) return;
    clearTimeout(this.heartbeatTimer);
    if (!this.send({ v: 1, type: "ping" })) return;
    this.pingSentAt = Date.now();
    const generation = this.generation;
    this.pongTimer = setTimeout(() => {
      if (this.current(generation)) this.fail();
    }, DEADLINE_MS);
  }

  private checkAccess(): void {
    this.disposeConnection();
    if (!this.canConnect()) return;
    const generation = this.generation;
    const chatId = this.chatId;
    this.checkingAccess = true;
    this.setState("reconnecting");
    this.armPhase(generation);
    // Promise.resolve also handles an adapter that throws before returning.
    void Promise.resolve()
      .then(() =>
        this.current(generation) ? this.options.verifyAccess(chatId) : "retry",
      )
      .then(
        (result) => {
          if (!this.current(generation)) return;
          if (result === "retry") this.fail();
          else {
            clearTimeout(this.phaseTimer);
            this.checkingAccess = false;
            this.permanent = result;
            this.setState(result);
          }
        },
        () => {
          if (this.current(generation)) this.fail();
        },
      );
  }

  private fail(): void {
    this.disposeConnection();
    if (!this.canConnect()) return;
    this.setState("reconnecting");
    const base = Math.min(30_000, 500 * 2 ** Math.min(this.attempts++, 6));
    const delay = Math.min(30_000, base * (0.8 + Math.random() * 0.4));
    this.retryTimer = setTimeout(() => {
      this.retryTimer = undefined;
      void this.connect();
    }, delay);
  }

  private disposeConnection(): void {
    ++this.generation;
    clearTimeout(this.phaseTimer);
    clearTimeout(this.subscriptionTimer);
    clearTimeout(this.heartbeatTimer);
    clearTimeout(this.pongTimer);
    clearTimeout(this.retryTimer);
    this.phaseTimer = this.subscriptionTimer = this.heartbeatTimer = undefined;
    this.pongTimer = this.retryTimer = undefined;
    this.ticketAbort?.abort();
    this.ticketAbort = null;
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onopen = socket.onmessage = socket.onerror = socket.onclose = null;
      // close() may throw for a browser implementation already tearing down.
      try {
        socket.close();
      } catch {
        // All callbacks and timers are already detached from this generation.
      }
    }
    this.hello = this.ready = this.checkingAccess = false;
    this.subscribedChat = this.requestedChat = null;
    this.pingSentAt = null;
    this.options.onReset();
  }
}
